import api from './api';

export interface CatalogMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface CatalogResponse<T> {
  success?: boolean;
  data: T;
  message?: string;
  meta?: CatalogMeta;
}

interface ServerMeta {
  page?: number;
  pageSize?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

interface ServerResponse<T> {
  success?: boolean;
  data: T;
  message?: string;
  meta?: ServerMeta;
}

export interface Product {
  id: string;
  sku: string;
  internalSku?: string;
  name: string;
  description?: string;
  category?: string;
  categoryId?: string;
  unit: string;
  unitPrice: number;
  allergens: string[];
  storageZone: string;
  storageRequirements?: string;
  minStock: number;
  maxStock: number;
  reorderPoint?: number;
  currentStock: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  inventory?: {
    onHand: number;
    reserved: number;
    available: number;
    binLocation?: string | null;
    barcode?: string | null;
    vendor?: string | null;
    lastCost: number;
    averageCost: number;
    totalValue: number;
  } | null;
}

export interface InventoryLot {
  id: string;
  productId: string;
  productName?: string;
  productSku?: string;
  lotNumber: string;
  quantity: number;
  receivedDate: string;
  expiryDate?: string;
  supplier?: string;
  locationId?: string;
  locationCode?: string;
}

export interface StockMovement {
  id: string;
  type: string;
  quantity: number;
  reason?: string;
  timestamp: string;
  actor?: string;
  referenceType?: string;
  referenceId?: string;
}

export interface ProductDetail extends Product {
  lots: InventoryLot[];
  recentMovements?: StockMovement[];
}

export interface CreateProductInput {
  sku?: string;
  internalSku?: string;
  name: string;
  description?: string;
  category?: string;
  categoryId?: string;
  unit: string;
  unitPrice?: number;
  allergens?: string[];
  storageZone?: string;
  storageRequirements?: string;
  minStock?: number;
  maxStock?: number;
  reorderPoint?: number;
  reorderQuantity?: number;
  isActive?: boolean;
}

export type UpdateProductInput = Partial<CreateProductInput>;

export interface StockAdjustmentInput {
  quantity: number;
  type: 'add' | 'remove' | 'set';
  reason: string;
  notes?: string;
}

export interface CatalogFilters {
  page?: number;
  pageSize?: number;
  category?: string;
  zone?: string;
  search?: string;
  lowStock?: boolean;
}

export interface LotFilters {
  expiringWithin?: number;
  search?: string;
}

function normalizeMeta(meta: ServerMeta | undefined, fallbackPageSize: number): CatalogMeta | undefined {
  if (!meta) return undefined;
  const pageSize = meta.pageSize ?? meta.limit ?? fallbackPageSize;
  return {
    page: meta.page ?? 1,
    pageSize,
    total: meta.total ?? 0,
    totalPages: meta.totalPages ?? Math.ceil((meta.total ?? 0) / Math.max(pageSize, 1)),
  };
}

function normalize<T>(payload: ServerResponse<T>, fallbackPageSize = 20): CatalogResponse<T> {
  return {
    success: payload.success,
    data: payload.data,
    message: payload.message,
    meta: normalizeMeta(payload.meta, fallbackPageSize),
  };
}

export const catalogApi = {
  getAll: async (filters: CatalogFilters = {}) => {
    if (filters.lowStock) {
      const response = await api.get<ServerResponse<Product[]>>('/products/low-stock', {
        params: {
          page: filters.page,
          limit: filters.pageSize,
          categoryId: filters.category,
          storageRequirement: filters.zone,
          search: filters.search,
        },
      });
      return { data: normalize(response.data, filters.pageSize ?? 20) };
    }

    const response = await api.get<ServerResponse<Product[]>>('/products', {
      params: {
        page: filters.page,
        limit: filters.pageSize,
        categoryId: filters.category,
        storageRequirement: filters.zone,
        search: filters.search,
        isActive: 'true',
      },
    });
    return { data: normalize(response.data, filters.pageSize ?? 20) };
  },

  getById: async (id: string) => {
    const response = await api.get<ServerResponse<ProductDetail>>(`/products/${id}`);
    return { data: normalize(response.data) };
  },

  create: async (data: CreateProductInput) => {
    const response = await api.post<ServerResponse<Product>>('/products', data);
    return { data: normalize(response.data) };
  },

  update: async (id: string, data: UpdateProductInput) => {
    const response = await api.patch<ServerResponse<Product>>(`/products/${id}`, data);
    return { data: normalize(response.data) };
  },

  adjustStock: async (id: string, adjustment: StockAdjustmentInput) => {
    const response = await api.post<ServerResponse<Product>>(
      `/inventory/product/${id}/adjust`,
      adjustment
    );
    return { data: normalize(response.data) };
  },

  getLots: async (productId: string) => {
    const response = await api.get<ServerResponse<InventoryLot[]>>(`/products/${productId}/lots`);
    return { data: normalize(response.data) };
  },

  getAllLots: async (filters: LotFilters = {}) => {
    const response = await api.get<ServerResponse<InventoryLot[]>>('/products/lots', {
      params: filters,
    });
    return { data: normalize(response.data) };
  },
};

export default catalogApi;
